/**
 * Personal settings, as tabs with an address each: `/settings` (Notifications)
 * and `/settings/connected-accounts`. Every signed-in user reaches this page
 * from the user menu; nothing here is admin-gated.
 */
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/AppShell.js';
import { NotificationPreferencesPage } from '../notifications/NotificationPreferencesPage.js';
import { ConnectedAccountsPanel } from './ConnectedAccountsPanel.js';
import { CONNECTED_ACCOUNTS_PATH } from './useConnectedAccountsLogic.js';

export type SettingsTab = 'notifications' | 'connected-accounts';

const TABS: ReadonlyArray<{ id: SettingsTab; label: string; to: string }> = [
  { id: 'notifications', label: 'Notifications', to: '/settings' },
  { id: 'connected-accounts', label: 'Connected accounts', to: CONNECTED_ACCOUNTS_PATH },
];

const TAB_BUTTON =
  'border-b-2 px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

export const SettingsPage = ({ tab = 'notifications' }: { tab?: SettingsTab }) => {
  const navigate = useNavigate();
  return (
    <AppShell>
      <h1 className="mb-6 text-2xl font-bold text-ink">Settings</h1>
      <div className="mb-6 flex gap-1 border-b border-outline" role="tablist" aria-label="Settings">
        {TABS.map((entry) => {
          const active = entry.id === tab;
          return (
            <button
              key={entry.id}
              type="button"
              id={`settings-${entry.id}-tab`}
              role="tab"
              aria-selected={active}
              aria-controls={`settings-${entry.id}-panel`}
              onClick={() => void navigate(entry.to)}
              className={`${TAB_BUTTON} ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {entry.label}
            </button>
          );
        })}
      </div>
      <section id={`settings-${tab}-panel`} role="tabpanel" aria-labelledby={`settings-${tab}-tab`}>
        {tab === 'notifications' ? (
          <NotificationPreferencesPage embedded />
        ) : (
          <ConnectedAccountsPanel />
        )}
      </section>
    </AppShell>
  );
};
